import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Building2, Plus, Trash2, Check, RotateCcw, PartyPopper, ArrowRight, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import CardBasic from '../../ui/Card.jsx';
import CreateCompanyModal from './CreateCompanyModal.jsx';
import { showMessage } from '../../../scripts/showMessage.js';

const CompaniesIndex = () => {
    const [companies, setCompanies] = useState([]);
    const [isMainAccount, setIsMainAccount] = useState(false);
    const [loading, setLoading] = useState(true);
    const [buyUrl, setBuyUrl] = useState('');
    const [deactivateTarget, setDeactivateTarget] = useState(null); // company object to deactivate
    const [switching, setSwitching] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pendingCompany, setPendingCompany] = useState(null);
    const [checkingPending, setCheckingPending] = useState(false);
    const [defaultCompanySid, setDefaultCompanySid] = useState(null);
    const [settingDefault, setSettingDefault] = useState(null);
    const [mainSid, setMainSid] = useState(null);
    const [canStartTrial, setCanStartTrial] = useState(false);
    const [trialDays, setTrialDays] = useState(14);
    const [showTrialModal, setShowTrialModal] = useState(false);

    const fetchCompanies = useCallback(async () => {
        try {
            const res = await fetch('/index.php/settings/companies/getCompanies');
            const data = await res.json();
            if (data.success) {
                setCompanies(data.companies);
                setIsMainAccount(data.is_main_account);
                setDefaultCompanySid(data.default_company_sid || null);
                setMainSid(data.main_user_sid || null);
                setCanStartTrial(!!data.can_start_trial);
                if (data.trial_days) setTrialDays(data.trial_days);
                if (data.buy_url) setBuyUrl(data.buy_url);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Firmen:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const checkPending = useCallback(async () => {
        try {
            const res = await fetch('/index.php/settings/companies/getPending');
            const data = await res.json();
            if (data.success && data.has_pending) {
                setPendingCompany(data.pending);
                return true;
            }
            setPendingCompany(null);
            return false;
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Nach Digistore24-Kauf: Auf Gutschein prüfen (IPN braucht ggf. einen Moment)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('purchase') === 'success') {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
            setCheckingPending(true);

            const pollForPending = async (attempt = 0) => {
                const found = await checkPending();
                if (found) {
                    setCheckingPending(false);
                    setShowWelcomeModal(true);
                } else if (attempt < 10) {
                    setTimeout(() => pollForPending(attempt + 1), 2000);
                } else {
                    setCheckingPending(false);
                    // Nach 20 Sekunden aufgeben — IPN evtl. noch nicht angekommen
                    setShowWelcomeModal(true);
                }
            };
            pollForPending();
        }
    }, []);

    const handleSwitch = async (userSid) => {
        setSwitching(userSid);
        try {
            const res = await fetch('/index.php/settings/companies/switchCompany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_sid: userSid }),
            });
            const data = await res.json();
            if (data.success) {
                window.location.reload();
            }
        } catch (err) {
            console.error('Fehler beim Wechseln:', err);
        } finally {
            setSwitching(null);
        }
    };

    // Deaktivieren OHNE Abo-Kündigung (nur Firma ausblenden)
    const handleDeactivateOnly = async () => {
        if (!deactivateTarget) return;
        setCancelLoading(true);
        try {
            const res = await fetch('/index.php/settings/companies/deleteCompany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_sid: deactivateTarget.user_sid }),
            });
            const data = await res.json();
            if (data.success) {
                setDeactivateTarget(null);
                fetchCompanies();
            }
        } catch (err) {
            console.error('Fehler beim Deaktivieren:', err);
        } finally {
            setCancelLoading(false);
        }
    };

    // Deaktivieren MIT Abo-Kündigung
    const handleDeactivateAndCancel = async () => {
        if (!deactivateTarget) return;
        setCancelLoading(true);
        try {
            const res = await fetch('/index.php/settings/companies/cancelSubscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_sid: deactivateTarget.user_sid }),
            });
            const data = await res.json();
            if (data.success) {
                setDeactivateTarget(null);
                fetchCompanies();
            }
        } catch (err) {
            console.error('Fehler beim Kündigen:', err);
        } finally {
            setCancelLoading(false);
        }
    };

    const handleSetupCompany = () => {
        setShowWelcomeModal(false);
        if (pendingCompany) {
            setShowCreateModal(true);
        }
    };

    const handleCompanyCreated = (newSid) => {
        setShowCreateModal(false);
        setPendingCompany(null);
        if (newSid) {
            handleSwitch(newSid);
        } else {
            fetchCompanies();
        }
    };

    const handleSetDefault = async (userSid) => {
        setSettingDefault(userSid);
        try {
            const sidToSend = userSid === mainSid ? null : userSid;
            const res = await fetch('/index.php/settings/companies/setDefaultCompany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_sid: sidToSend }),
            });
            const data = await res.json();
            if (data.success) {
                setDefaultCompanySid(sidToSend);
                const selectedCompany = companies.find(c => c.user_sid === userSid);
                const label = selectedCompany?.label || 'Hauptaccount';
                showMessage(`<strong>${label}</strong> wird beim nächsten Login automatisch aktiviert.`, 'success');
            }
        } catch (err) {
            console.error('Fehler beim Setzen der Standard-Firma:', err);
        } finally {
            setSettingDefault(null);
        }
    };

    const activeCompanies = companies.filter((c) => c.active === 1);
    const inactiveCompanies = companies.filter((c) => c.active === 0);

    return (
        <div>
            <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <a
                            href="index.php/settings"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                fontSize: '0.875rem',
                                color: 'var(--text-secondary, #64748b)',
                                marginBottom: '0.75rem',
                                textDecoration: 'none',
                            }}
                        >
                            <ArrowLeft size={16} />
                            Zurück zu Einstellungen
                        </a>
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <Building2 size={32} className="icon-jobs" />
                            Firmen
                        </h1>
                        <p className="text-muted mb-0">
                            Verwalte deine Firmen und wechsle zwischen ihnen.
                        </p>
                    </div>
                    {isMainAccount && buyUrl && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                            {canStartTrial && (
                                <button
                                    type="button"
                                    onClick={() => setShowTrialModal(true)}
                                    className="btn btn-primary d-flex align-items-center gap-2 shadow"
                                >
                                    <Sparkles size={18} />
                                    {trialDays} Tage kostenlos testen
                                </button>
                            )}
                            <a
                                href={buyUrl}
                                className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
                            >
                                <Plus size={20} />
                                Neue Firma hinzufügen
                            </a>
                            <span style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary, #64748b)',
                            }}>
                                12,00 € / Monat zzgl. MwSt. pro weitere Firma
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Hinweis wenn Gutschein noch nicht eingelöst */}
            {checkingPending && (
                <div className="alert d-flex align-items-center gap-2 mb-3" style={{
                    background: 'var(--bg-secondary, #f5f7f9)',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderRadius: '10px',
                    color: 'var(--text-secondary, #64748b)',
                    fontSize: '0.875rem',
                }}>
                    <div className="spinner-border spinner-border-sm" role="status" />
                    Dein Kauf wird verarbeitet...
                </div>
            )}

            <CardBasic>
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-muted" role="status" />
                    </div>
                ) : companies.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Keine Firmen gefunden.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {activeCompanies.length > 1 && (
                            <div style={{
                                fontSize: '0.8125rem',
                                color: 'var(--text-secondary, #64748b)',
                                marginBottom: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                            }}>
                                Die markierte Firma wird nach dem Login automatisch aktiviert.
                            </div>
                        )}
                        {activeCompanies.map((company) => (
                            <CompanyRow
                                key={company.user_sid}
                                company={company}
                                isMainAccount={isMainAccount}
                                switching={switching}
                                onSwitch={handleSwitch}
                                onDeactivate={(c) => setDeactivateTarget(c)}
                                isDefault={company.user_sid === defaultCompanySid || (!defaultCompanySid && company.account_type === 'main')}
                                onSetDefault={handleSetDefault}
                                settingDefault={settingDefault}
                                showDefaultRadio={activeCompanies.length > 1}
                                buyUrl={buyUrl}
                            />
                        ))}

                        {inactiveCompanies.length > 0 && (
                            <>
                                <div style={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    color: 'var(--text-secondary, #64748b)',
                                    marginTop: '1rem',
                                    marginBottom: '0.25rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}>
                                    Deaktiviert
                                </div>
                                {inactiveCompanies.map((company) => (
                                    <div
                                        key={company.user_sid}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '0.75rem 1.25rem',
                                            borderRadius: '10px',
                                            border: '1px dashed var(--border-light, #e2e8f0)',
                                            background: 'var(--bg-card, #ffffff)',
                                            opacity: 0.6,
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '10px',
                                                background: 'var(--bg-secondary, #f5f7f9)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <Building2 size={20} style={{ color: 'var(--text-secondary, #64748b)' }} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary, #2c3e50)' }}>
                                                    {company.label}
                                                </div>
                                                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)' }}>
                                                    Deaktiviert
                                                </div>
                                            </div>
                                        </div>
                                        {isMainAccount && buyUrl && (
                                            <a
                                                href={buyUrl}
                                                className="btn btn-sm btn-outline-primary"
                                                style={{ borderRadius: '8px', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                                            >
                                                <RotateCcw size={14} />
                                                Abo erneuern
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </CardBasic>

            {/* Deaktivieren-Modal mit Abo-Kündigungs-Option */}
            <Modal show={!!deactivateTarget} onHide={() => !cancelLoading && setDeactivateTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Firma deaktivieren
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
                        <AlertTriangle size={20} style={{ color: '#e67e22', flexShrink: 0, marginTop: '2px' }} />
                        <div>
                            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary, #2c3e50)' }}>
                                Möchtest du <strong>{deactivateTarget?.label}</strong> deaktivieren?
                            </p>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)', lineHeight: 1.5 }}>
                                Die Daten bleiben erhalten und die Firma kann jederzeit wieder aktiviert werden.
                            </p>
                        </div>
                    </div>

                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)', marginTop: '0.75rem', lineHeight: 1.5 }}>
                        Das monatliche Abo für diese Firma wird ebenfalls gekündigt.
                    </p>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid var(--border-light, #e2e8f0)' }}>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setDeactivateTarget(null)}
                        disabled={cancelLoading}
                        style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeactivateAndCancel}
                        disabled={cancelLoading}
                        style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                    >
                        {cancelLoading ? 'Wird verarbeitet...' : 'Deaktivieren & Abo kündigen'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Welcome Modal nach Digistore24-Kauf */}
            <Modal show={showWelcomeModal} onHide={() => setShowWelcomeModal(false)} centered>
                <Modal.Body style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{
                        width: '64px', height: '64px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #5b7c8d 0%, #7ba2b8 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1.25rem',
                    }}>
                        <PartyPopper size={32} style={{ color: '#fff' }} />
                    </div>
                    <h4 style={{ fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-primary, #2c3e50)' }}>
                        Vielen Dank für deinen Kauf!
                    </h4>
                    {pendingCompany ? (
                        <>
                            <p style={{
                                fontSize: '0.9375rem', color: 'var(--text-secondary, #64748b)',
                                lineHeight: 1.6, marginBottom: '1.5rem',
                            }}>
                                Du kannst jetzt deine neue Firma einrichten — gib ihr einen
                                Namen und lege fest, ob Kontakte und Locations mit deinem
                                Hauptaccount geteilt werden sollen.
                            </p>
                            <Button
                                variant="primary"
                                onClick={handleSetupCompany}
                                style={{
                                    borderRadius: '8px', padding: '0.625rem 1.5rem',
                                    fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                                }}
                            >
                                Firma einrichten
                                <ArrowRight size={18} />
                            </Button>
                        </>
                    ) : (
                        <>
                            <p style={{
                                fontSize: '0.9375rem', color: 'var(--text-secondary, #64748b)',
                                lineHeight: 1.6, marginBottom: '0.75rem',
                            }}>
                                Dein Kauf wird noch verarbeitet. Bitte lade die Seite
                                in ein paar Sekunden erneut.
                            </p>
                            <Button
                                variant="outline-secondary"
                                onClick={() => window.location.reload()}
                                style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                            >
                                Seite neu laden
                            </Button>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* Create Company Modal */}
            <CreateCompanyModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onCreated={handleCompanyCreated}
                pendingId={pendingCompany?.id}
            />

            {/* Testfirma-Modal (14 Tage kostenlos) */}
            <CreateCompanyModal
                show={showTrialModal}
                onHide={() => setShowTrialModal(false)}
                onCreated={(newSid) => {
                    setShowTrialModal(false);
                    setCanStartTrial(false);
                    if (newSid) {
                        handleSwitch(newSid);
                    } else {
                        fetchCompanies();
                    }
                }}
                trial
                trialDays={trialDays}
            />
        </div>
    );
};

const CompanyRow = ({
    company, isMainAccount, switching,
    onSwitch, onDeactivate,
    isDefault, onSetDefault, settingDefault, showDefaultRadio, buyUrl,
}) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 1.25rem', borderRadius: '10px',
        border: company.is_current ? '2px solid var(--accent-slate, #5b7c8d)' : '1px solid var(--border-light, #e2e8f0)',
        background: company.is_current ? 'var(--bg-secondary, #f5f7f9)' : 'var(--bg-card, #ffffff)',
        transition: 'all 0.2s ease',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {showDefaultRadio && (
                <input
                    type="radio"
                    name="defaultCompany"
                    checked={isDefault}
                    onChange={() => onSetDefault(company.user_sid)}
                    disabled={settingDefault !== null}
                    style={{
                        width: '18px', height: '18px', cursor: 'pointer',
                        accentColor: 'var(--accent-slate, #5b7c8d)', flexShrink: 0,
                    }}
                    title="Standard nach Login"
                />
            )}
            <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: company.is_current ? 'var(--accent-slate, #5b7c8d)' : 'var(--bg-secondary, #f5f7f9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {company.is_current
                    ? <Check size={20} style={{ color: '#fff' }} />
                    : <Building2 size={20} style={{ color: 'var(--text-secondary, #64748b)' }} />
                }
            </div>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary, #2c3e50)' }}>
                        {company.label}
                    </span>
                    {company.is_trial && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            fontSize: '0.6875rem', fontWeight: 600,
                            padding: '0.125rem 0.5rem', borderRadius: '10px',
                            background: company.trial_expired ? 'rgba(220, 53, 69, 0.1)' : 'rgba(91, 124, 141, 0.12)',
                            color: company.trial_expired ? 'var(--status-error, #dc3545)' : 'var(--accent-slate, #5b7c8d)',
                        }}>
                            <Clock size={11} />
                            {company.trial_expired
                                ? 'Test abgelaufen'
                                : `Test — noch ${company.trial_days_left} ${company.trial_days_left === 1 ? 'Tag' : 'Tage'}`}
                        </span>
                    )}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)' }}>
                    {company.account_type === 'main' ? 'Hauptaccount' : 'Firma'}
                    {company.is_current && ' — aktiv'}
                    {company.is_trial && company.trial_expired && ' — nur Lesezugriff'}
                </div>
            </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {company.is_trial && buyUrl && (
                <a
                    href={buyUrl}
                    className="btn btn-sm btn-primary"
                    style={{ borderRadius: '8px', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                    <Sparkles size={14} />
                    Kostenpflichtig übernehmen
                </a>
            )}
            {!company.is_current && (
                <button
                    onClick={() => onSwitch(company.user_sid)}
                    disabled={switching === company.user_sid}
                    className="btn btn-sm btn-outline-primary"
                    style={{ borderRadius: '8px', fontSize: '0.8125rem' }}
                >
                    {switching === company.user_sid ? 'Wechsle...' : 'Wechseln'}
                </button>
            )}
            {isMainAccount && company.account_type === 'company_sub' && (
                <button onClick={() => onDeactivate(company)}
                    className="btn btn-sm btn-outline-danger"
                    style={{ borderRadius: '8px', padding: '0.25rem 0.5rem' }}
                    title="Firma deaktivieren">
                    <Trash2 size={14} />
                </button>
            )}
        </div>
    </div>
);

export default CompaniesIndex;
